export const colorFade = (hex: string, saturation: number = 0.25, brightness: number = 1): string => {
  const clean = hex.replace(/^#/, "");
  const r0 = parseInt(clean.slice(0, 2), 16) / 255;
  const g0 = parseInt(clean.slice(2, 4), 16) / 255;
  const b0 = parseInt(clean.slice(4, 6), 16) / 255;

  // 2. RGB → HSV/HSB
  const max = Math.max(r0, g0, b0);
  const min = Math.min(r0, g0, b0);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    switch (max) {
      case r0:
        h = (g0 - b0) / d + (g0 < b0 ? 6 : 0);
        break;
      case g0:
        h = (b0 - r0) / d + 2;
        break;
      case b0:
        h = (r0 - g0) / d + 4;
        break;
    }
    h /= 6;
  }

  const s = saturation;
  const v = brightness;

  // 4. HSV/HSB → RGB
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  switch (i % 6) {
    case 0:
      r1 = v;
      g1 = t;
      b1 = p;
      break;
    case 1:
      r1 = q;
      g1 = v;
      b1 = p;
      break;
    case 2:
      r1 = p;
      g1 = v;
      b1 = t;
      break;
    case 3:
      r1 = p;
      g1 = q;
      b1 = v;
      break;
    case 4:
      r1 = t;
      g1 = p;
      b1 = v;
      break;
    case 5:
      r1 = v;
      g1 = p;
      b1 = q;
      break;
  }

  // 5. Convert back to hex
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
};

/**
 * Blend a sRGB hex color with white for an opaque result (Excel has no RGBA fills).
 * Matches the visual intent of 20% platform tint over a white background.
 */
export function blendHexWithWhite(hex: string, opacity = 0.2): string | null {
  const normalized = hex.trim().replace(/^#/, "");
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const blend = (channel: number) => Math.round(255 * (1 - opacity) + channel * opacity);
  const toHex = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
  return `#${toHex(blend(r))}${toHex(blend(g))}${toHex(blend(b))}`;
}

/* Fills and borders written into worksheet cells. */
export const WORKSHEET_BORDER_COLOR = "#D0D5DD";
/** Report tables are ruled in two weights: light inside the grid, darker around its edges. */
export const INNER_BORDER_COLOR = "#808080";
export const OUTER_BORDER_COLOR = "#4b4b4b";
export const WORKSHEET_HEADER_FILL = "#FAFBFE";
export const WORKSHEET_SPACER_FILL = "#F2F3F6";
export const WORKSHEET_GRAYED_OUT_FILL = "#F0F0F0";

/** Green reads as "settled" in both places it lands: an awarded bid group, and an under-budget delta. */
const WORKSHEET_GREEN = "#32b186";

export const AWARDED_BORDER_COLOR = WORKSHEET_GREEN;
export const UNDER_BUDGET_TEXT_COLOR = WORKSHEET_GREEN;
export const OVER_BUDGET_TEXT_COLOR = "#e22828";

/**
 * Series colors for generated workbook charts, in assignment order: the eight brand accents, then
 * the same eight lightened, then darkened. Consumed cyclically, so more series than entries wraps.
 */
export const dataVizColorArray = [
  "#0161E8",
  "#00DBE8",
  "#FF019A",
  "#6206F4",
  "#FF771A",
  "#E5C700",
  "#01A9F4",
  "#1C7D82",
  "#3388ff",
  "#ff66bf",
  "#8d45f9",
  "#ffa25b",
  "#66f1ff",
  "#fff14a",
  "#66c5ff",
  "#59a695",
  "#0046a4",
  "#b3006c",
  "#44049e",
  "#b35300",
  "#009ba6",
  "#a18f00",
  "#0077a9",
  "#124f53",
];
